import React, { useState, useEffect } from 'react';
import {
  Typography,
  Paper,
  Box,
  Alert,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
  InputAdornment,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Avatar,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Block as RevokeIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  VpnKey as VpnKeyIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../animations/variants';
import { toast } from 'react-toastify';
import apiService from '../services/api';
import { getCustomerIdFromSubdomain, getSCIMBaseURL } from '../utils/constants';
import { filterInput } from '../utils/inputSanitization';

// Theme color for SCIM Configuration page - using Purple from design system
const accentColor = '#9B59B6';
const accentColorLight = '#F5EBF8';
const accentColorHover = '#8E44AD';

// Consistent button styles
const buttonStyles = {
  primary: {
    backgroundColor: accentColor,
    fontFamily: 'Metropolis, sans-serif',
    fontWeight: 600,
    textTransform: 'none',
    borderRadius: '6px',
    px: 3,
    '&:hover': { backgroundColor: accentColorHover },
    '&:disabled': {
      backgroundColor: '#E5E5E5',
      color: '#999999',
    },
  },
  cancel: {
    borderColor: '#E5E5E5',
    color: '#666666',
    fontFamily: 'Metropolis, sans-serif',
    fontWeight: 600,
    textTransform: 'none',
    borderRadius: '6px',
    px: 3,
    '&:hover': {
      borderColor: '#999999',
      backgroundColor: '#F9FBFD',
    },
  },
  danger: {
    backgroundColor: '#E74C3C',
    fontFamily: 'Metropolis, sans-serif',
    fontWeight: 600,
    textTransform: 'none',
    borderRadius: '6px',
    px: 3,
    '&:hover': { backgroundColor: '#C0392B' },
    '&:disabled': {
      backgroundColor: '#E5E5E5',
      color: '#999999',
    },
  },
  warning: {
    backgroundColor: '#F39C12',
    fontFamily: 'Metropolis, sans-serif',
    fontWeight: 600,
    textTransform: 'none',
    borderRadius: '6px',
    px: 3,
    '&:hover': { backgroundColor: '#E67E22' },
    '&:disabled': {
      backgroundColor: '#E5E5E5',
      color: '#999999',
    },
  },
};

const dialogPaperProps = {
  sx: {
    borderRadius: '12px',
    border: '1px solid #E5E5E5',
    backgroundColor: '#FFFFFF',
  },
};

// Minimalist input style
const inputStyle = {
  '& .MuiOutlinedInput-root': {
    fontFamily: 'Metropolis, sans-serif',
    backgroundColor: '#FFFFFF',
    '& fieldset': {
      borderColor: '#E5E5E5',
    },
    '&:hover fieldset': {
      borderColor: accentColor,
    },
    '&.Mui-focused fieldset': {
      borderColor: accentColor,
    },
  },
  '& .MuiInputLabel-root': {
    fontFamily: 'Metropolis, sans-serif',
    color: '#666666',
    '&.Mui-focused': {
      color: accentColor,
    },
  },
  '& .MuiFormHelperText-root': {
    fontFamily: 'Metropolis, sans-serif',
    color: '#999999',
  },
};

const SCIMConfiguration = () => {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openGenerateDialog, setOpenGenerateDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openRevokeDialog, setOpenRevokeDialog] = useState(false);
  const [selectedToken, setSelectedToken] = useState(null);
  const [expiresInDays, setExpiresInDays] = useState('');
  const [generatedToken, setGeneratedToken] = useState(null);
  const [copiedUrl, setCopiedUrl] = useState(false);

  const customerId = getCustomerIdFromSubdomain();
  const realm = customerId || 'aws-sso';
  const scimBaseUrl = getSCIMBaseURL();

  useEffect(() => {
    loadTokens();
  }, []);

  const loadTokens = async () => {
    setLoading(true);
    try {
      const response = await apiService.listCustomerSCIMTokens(customerId);

      if (response.success) {
        setTokens(response.data.tokens || []);
      } else {
        toast.error(response.message || 'Failed to load SCIM tokens');
      }
    } catch (err) {
      // Silently handle "not found" errors - no tokens is a valid state
      const errorMessage = err?.message?.toLowerCase() || '';
      const isNotFoundError =
        errorMessage.includes('404') ||
        errorMessage.includes('not found') ||
        err?.response?.status === 404;

      if (!isNotFoundError) {
        toast.error(err.message || 'Failed to load SCIM tokens');
      }
      setTokens([]);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateToken = async () => {
    setLoading(true);
    try {
      const expiresIn = expiresInDays ? parseInt(expiresInDays) : null;

      const response = await apiService.generateCustomerSCIMToken(customerId, expiresIn);

      if (response.success) {
        toast.success('SCIM token generated successfully');
        setGeneratedToken(response.data.token || response.data);
        setOpenGenerateDialog(false);
        setExpiresInDays('');
        await loadTokens();
      } else {
        toast.error(response.message || 'Failed to generate SCIM token');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to generate SCIM token');
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeToken = async () => {
    if (!selectedToken) return;

    setLoading(true);
    try {
      const response = await apiService.revokeCustomerSCIMToken(customerId, selectedToken.token);

      if (response.success) {
        toast.success('SCIM token revoked successfully');
        setOpenRevokeDialog(false);
        setSelectedToken(null);
        await loadTokens();
      } else {
        toast.error(response.message || 'Failed to revoke SCIM token');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to revoke SCIM token');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteToken = async () => {
    if (!selectedToken) return;

    setLoading(true);
    try {
      const response = await apiService.deleteCustomerSCIMToken(customerId, selectedToken.token);

      if (response.success) {
        toast.success('SCIM token deleted successfully');
        setOpenDeleteDialog(false);
        setSelectedToken(null);
        await loadTokens();
      } else {
        toast.error(response.message || 'Failed to delete SCIM token');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to delete SCIM token');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, label = 'Text') => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(scimBaseUrl);
    setCopiedUrl(true);
    toast.success('SCIM URL copied to clipboard');
    setTimeout(() => setCopiedUrl(false), 2000);
  };

  // Mask token to show first 6 and last 4 characters with fixed 8 asterisks
  const maskToken = (token) => {
    if (!token) return '';
    const visibleStart = 6;
    const visibleEnd = 4;
    const maskLength = 8;

    if (token.length <= visibleStart + visibleEnd) {
      return `${token.substring(0, 3)}${'*'.repeat(maskLength)}${token.substring(token.length - 2)}`;
    }

    return `${token.substring(0, visibleStart)}${'*'.repeat(maskLength)}${token.substring(token.length - visibleEnd)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const isTokenExpired = (expiresAt) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  const isTokenRevoked = (token) => {
    if (token.isActive !== undefined) {
      return !token.isActive;
    }
    return token.revoked === true || token.status === 'REVOKED';
  };

  const getTokenStatus = (token) => {
    if (isTokenRevoked(token)) {
      return { label: 'Revoked', color: '#E74C3C', bgColor: '#FDEDEC' };
    }
    if (isTokenExpired(token.expiresAt)) {
      return { label: 'Expired', color: '#E74C3C', bgColor: '#FDEDEC' };
    }
    if (token.expiresAt) {
      return { label: 'Active', color: '#27AE60', bgColor: '#E8F8F0' };
    }
    return { label: 'Active (No Expiry)', color: '#4698D3', bgColor: '#E8F4FC' };
  };

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      style={{
        minHeight: '100vh',
        paddingTop: 32,
        paddingBottom: 32,
        paddingLeft: 32,
        paddingRight: 32,
        backgroundColor: '#F9FBFD',
      }}
    >
      {/* Page Header */}
      <motion.div variants={sectionVariants} style={{ marginBottom: 24 }}>
      <Paper
        elevation={0}
        sx={{
          backgroundColor: '#FFFFFF',
          borderRadius: '8px',
          border: '1px solid #E5E5E5',
          p: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              sx={{
                backgroundColor: accentColor,
                width: 48,
                height: 48,
              }}
            >
              <VpnKeyIcon sx={{ fontSize: 24 }} />
            </Avatar>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontFamily: 'Bitter, Georgia, serif',
                  fontWeight: 600,
                  fontSize: '1.75rem',
                  color: '#253E66',
                }}
              >
                SCIM Configuration
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontSize: '0.875rem',
                  color: '#999999',
                  fontWeight: 500,
                }}
              >
                Configure SCIM for automated user provisioning
              </Typography>
            </Box>
          </Box>

          <Tooltip title="Refresh Tokens">
            <IconButton
              onClick={loadTokens}
              disabled={loading}
              sx={{
                color: '#666666',
                backgroundColor: '#F9FBFD',
                border: '1px solid #E5E5E5',
                borderRadius: '6px',
                '&:hover': {
                  backgroundColor: '#F0F4F8',
                  borderColor: accentColor,
                  color: accentColor,
                },
                '&:disabled': {
                  color: '#BDBDBD',
                  backgroundColor: '#F9FBFD',
                },
              }}
            >
              {loading ? (
                <CircularProgress size={20} sx={{ color: accentColor }} />
              ) : (
                <RefreshIcon />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>
      </motion.div>

      {/* SCIM Base URL Card */}
      <motion.div variants={sectionVariants} style={{ marginBottom: 24 }}>
      <Card
        elevation={0}
        sx={{
          backgroundColor: '#FFFFFF',
          border: '1px solid #E5E5E5',
          borderRadius: '8px',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
            <InfoIcon sx={{ fontSize: 24, color: '#4698D3' }} />
            <Typography
              variant="h6"
              sx={{
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                fontSize: '1rem',
                color: '#253E66',
              }}
            >
              SCIM Endpoint Configuration
            </Typography>
          </Box>

          <Divider sx={{ mb: 3, borderColor: '#E5E5E5' }} />

          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{
                fontFamily: 'Metropolis, sans-serif',
                fontSize: '0.8rem',
                color: '#999999',
                fontWeight: 500,
                mb: 1,
              }}
            >
              SCIM Base URL
            </Typography>
            <Box
              sx={{
                backgroundColor: '#F9FBFD',
                borderRadius: '6px',
                p: 2,
                border: '1px solid #E5E5E5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.9rem',
                  color: '#253E66',
                  wordBreak: 'break-all',
                  flex: 1,
                }}
              >
                {scimBaseUrl}
              </Typography>
              <Tooltip title={copiedUrl ? 'Copied!' : 'Copy URL'}>
                <IconButton
                  onClick={handleCopyUrl}
                  size="small"
                  sx={{
                    color: copiedUrl ? '#27AE60' : accentColor,
                    backgroundColor: copiedUrl ? '#E8F8F0' : accentColorLight,
                    border: '1px solid #E5E5E5',
                    borderRadius: '6px',
                    ml: 2,
                    '&:hover': {
                      backgroundColor: copiedUrl ? '#D4EFDF' : '#EBD6F0',
                    },
                  }}
                >
                  {copiedUrl ? <CheckCircleIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{
                fontFamily: 'Metropolis, sans-serif',
                fontSize: '0.8rem',
                color: '#999999',
                fontWeight: 500,
                mb: 1,
              }}
            >
              Authentication Method
            </Typography>
            <Box
              sx={{
                backgroundColor: '#F9FBFD',
                borderRadius: '6px',
                p: 2,
                border: '1px solid #E5E5E5',
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontSize: '0.9rem',
                  color: '#253E66',
                }}
              >
                Bearer Token
              </Typography>
            </Box>
          </Box>

          <Alert
            severity="info"
            icon={<InfoIcon />}
            sx={{
              backgroundColor: '#E8F4FC',
              border: '1px solid #4698D320',
              borderRadius: '6px',
              '& .MuiAlert-icon': { color: '#4698D3' },
              '& .MuiAlert-message': {
                fontFamily: 'Metropolis, sans-serif',
                color: '#253E66',
              },
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Configuration Steps:
            </Typography>
            <Typography variant="body2" component="div">
              1. Generate a SCIM token below
              <br />
              2. Configure your identity provider with the SCIM endpoint URL
              <br />
              3. Use the generated token as the Bearer token for authentication
              <br />
              4. Test the connection from your identity provider
            </Typography>
          </Alert>
        </CardContent>
      </Card>
      </motion.div>

      {/* Token Management Card */}
      <motion.div variants={sectionVariants}>
      <Card
        elevation={0}
        sx={{
          backgroundColor: '#FFFFFF',
          border: '1px solid #E5E5E5',
          borderRadius: '8px',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <VpnKeyIcon sx={{ fontSize: 24, color: accentColor }} />
              <Typography
                variant="h6"
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 600,
                  fontSize: '1rem',
                  color: '#253E66',
                }}
              >
                SCIM Tokens
              </Typography>
              <Chip
                label={`${tokens.length} token${tokens.length !== 1 ? 's' : ''}`}
                size="small"
                sx={{
                  backgroundColor: accentColorLight,
                  color: accentColor,
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 500,
                }}
              />
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenGenerateDialog(true)}
              disabled={loading}
              sx={buttonStyles.primary}
            >
              Generate Token
            </Button>
          </Box>

          <Divider sx={{ mb: 2, borderColor: '#E5E5E5' }} />

          {loading && tokens.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress sx={{ color: accentColor }} />
            </Box>
          ) : tokens.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <VpnKeyIcon sx={{ fontSize: 64, color: '#E5E5E5', mb: 2 }} />
              <Typography
                variant="h6"
                sx={{
                  fontFamily: 'Bitter, Georgia, serif',
                  color: '#999999',
                  mb: 1,
                }}
              >
                No SCIM Tokens
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontSize: '0.875rem',
                  color: '#999999',
                }}
              >
                Generate your first token to enable SCIM provisioning.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontFamily: 'Metropolis, sans-serif',
                        fontWeight: 600,
                        color: '#253E66',
                        borderColor: '#E5E5E5',
                      }}
                    >
                      Token
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: 'Metropolis, sans-serif',
                        fontWeight: 600,
                        color: '#253E66',
                        borderColor: '#E5E5E5',
                      }}
                    >
                      Created
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: 'Metropolis, sans-serif',
                        fontWeight: 600,
                        color: '#253E66',
                        borderColor: '#E5E5E5',
                      }}
                    >
                      Expires
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: 'Metropolis, sans-serif',
                        fontWeight: 600,
                        color: '#253E66',
                        borderColor: '#E5E5E5',
                      }}
                    >
                      Status
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontFamily: 'Metropolis, sans-serif',
                        fontWeight: 600,
                        color: '#253E66',
                        borderColor: '#E5E5E5',
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tokens.map((token, index) => {
                    const status = getTokenStatus(token);
                    const maskedToken = maskToken(token.token);
                    return (
                      <TableRow
                        key={token.token}
                        sx={{
                          '@keyframes fadeInRow': {
                            from: { opacity: 0, transform: 'translateY(8px)' },
                            to: { opacity: 1, transform: 'translateY(0)' },
                          },
                          animation: `fadeInRow 0.3s ease-out ${index * 0.03}s both`,
                          '&:hover': { backgroundColor: '#F9FBFD' },
                        }}
                      >
                        <TableCell sx={{ borderColor: '#E5E5E5' }}>
                          <Typography
                            sx={{
                              fontFamily: 'monospace',
                              fontSize: '0.875rem',
                              color: '#253E66',
                            }}
                          >
                            {maskedToken}
                          </Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            fontFamily: 'Metropolis, sans-serif',
                            color: '#666666',
                            borderColor: '#E5E5E5',
                          }}
                        >
                          {formatDate(token.createdAt)}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontFamily: 'Metropolis, sans-serif',
                            color: '#666666',
                            borderColor: '#E5E5E5',
                          }}
                        >
                          {formatDate(token.expiresAt)}
                        </TableCell>
                        <TableCell sx={{ borderColor: '#E5E5E5' }}>
                          <Chip
                            label={status.label}
                            size="small"
                            sx={{
                              backgroundColor: status.bgColor,
                              color: status.color,
                              fontFamily: 'Metropolis, sans-serif',
                              fontWeight: 500,
                              border: `1px solid ${status.color}20`,
                            }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ borderColor: '#E5E5E5' }}>
                          {!isTokenRevoked(token) && (
                            <Tooltip title="Revoke Token">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedToken(token);
                                  setOpenRevokeDialog(true);
                                }}
                                sx={{
                                  color: '#F39C12',
                                  backgroundColor: '#FEF9E7',
                                  border: '1px solid #E5E5E5',
                                  borderRadius: '6px',
                                  mr: 1,
                                  '&:hover': {
                                    backgroundColor: '#FCF3CF',
                                    borderColor: '#F39C12',
                                  },
                                }}
                              >
                                <RevokeIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Delete Token">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedToken(token);
                                setOpenDeleteDialog(true);
                              }}
                              sx={{
                                color: '#E74C3C',
                                backgroundColor: '#FDEDEC',
                                border: '1px solid #E5E5E5',
                                borderRadius: '6px',
                                '&:hover': {
                                  backgroundColor: '#FADBD8',
                                  borderColor: '#E74C3C',
                                },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
      </motion.div>

      {/* Generate Token Dialog */}
      <Dialog
        open={openGenerateDialog}
        onClose={() => setOpenGenerateDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={dialogPaperProps}
      >
        <DialogTitle
          sx={{
            backgroundColor: accentColorLight,
            borderBottom: '1px solid #E5E5E5',
            fontFamily: 'Bitter, Georgia, serif',
            fontWeight: 600,
            color: '#253E66',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ backgroundColor: accentColor, width: 40, height: 40 }}>
              <VpnKeyIcon />
            </Avatar>
            Generate SCIM Token
          </Box>
          <IconButton
            onClick={() => setOpenGenerateDialog(false)}
            sx={{
              color: '#666666',
              '&:hover': { backgroundColor: '#F9FBFD' },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, backgroundColor: '#FFFFFF' }}>
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
          <TextField
            fullWidth
            label="Expires In (Days)"
            value={expiresInDays}
            onChange={(e) => setExpiresInDays(filterInput(e.target.value, 'NUMERIC'))}
            helperText="Leave empty for a token that never expires"
            placeholder="e.g. 30, 90, 365"
            sx={inputStyle}
          />
          <Alert
            severity="info"
            icon={<InfoIcon />}
            sx={{
              mt: 2,
              backgroundColor: '#E8F4FC',
              border: '1px solid #4698D320',
              borderRadius: '6px',
              '& .MuiAlert-icon': { color: '#4698D3' },
              '& .MuiAlert-message': {
                fontFamily: 'Metropolis, sans-serif',
                color: '#253E66',
              },
            }}
          >
            <Typography variant="body2">
              For security reasons, it's recommended to set an expiration period for your tokens.
            </Typography>
          </Alert>
          </motion.div>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0, backgroundColor: '#FFFFFF', gap: 2 }}>
          <Button
            onClick={() => setOpenGenerateDialog(false)}
            disabled={loading}
            variant="outlined"
            sx={buttonStyles.cancel}
          >
            Cancel
          </Button>
          <Button
            onClick={handleGenerateToken}
            variant="contained"
            disabled={loading}
            sx={buttonStyles.primary}
            startIcon={loading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <AddIcon />}
          >
            {loading ? 'Generating...' : 'Generate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Generated Token Display Dialog */}
      <Dialog
        open={!!generatedToken}
        onClose={() => setGeneratedToken(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={dialogPaperProps}
      >
        <DialogTitle
          sx={{
            backgroundColor: '#E8F8F0',
            borderBottom: '1px solid #E5E5E5',
            fontFamily: 'Bitter, Georgia, serif',
            fontWeight: 600,
            color: '#27AE60',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <CheckCircleIcon />
          Token Generated Successfully
        </DialogTitle>
        <DialogContent sx={{ pt: 3, backgroundColor: '#FFFFFF' }}>
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
          <Alert
            severity="warning"
            sx={{
              mb: 3,
              backgroundColor: '#FEF9E7',
              border: '1px solid #F39C1220',
              borderRadius: '6px',
              '& .MuiAlert-icon': { color: '#F39C12' },
              '& .MuiAlert-message': {
                fontFamily: 'Metropolis, sans-serif',
                color: '#253E66',
              },
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Important: Copy this token now and store it securely. The full token is only shown once!
            </Typography>
          </Alert>

          <Typography
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              fontSize: '0.8rem',
              color: '#999999',
              fontWeight: 500,
              mb: 1,
            }}
          >
            Generated Token
          </Typography>
          <Box
            sx={{
              backgroundColor: '#F9FBFD',
              borderRadius: '6px',
              p: 2,
              border: '1px solid #E5E5E5',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 2,
            }}
          >
            <Typography
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.85rem',
                color: '#253E66',
                wordBreak: 'break-all',
                flex: 1,
              }}
            >
              {generatedToken?.token || generatedToken}
            </Typography>
            <Tooltip title="Copy Token">
              <IconButton
                onClick={() => {
                  copyToClipboard(generatedToken?.token || generatedToken, 'Token');
                }}
                size="small"
                sx={{
                  color: accentColor,
                  backgroundColor: accentColorLight,
                  border: '1px solid #E5E5E5',
                  borderRadius: '6px',
                  flexShrink: 0,
                  '&:hover': {
                    backgroundColor: '#EBD6F0',
                  },
                }}
              >
                <CopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Alert
            severity="info"
            sx={{
              mt: 3,
              backgroundColor: '#E8F4FC',
              border: '1px solid #4698D320',
              borderRadius: '6px',
              '& .MuiAlert-icon': { color: '#4698D3' },
              '& .MuiAlert-message': {
                fontFamily: 'Metropolis, sans-serif',
                color: '#253E66',
              },
            }}
          >
            <Typography variant="body2">
              Once you close this dialog, only a masked version of this token will be visible for security purposes.
            </Typography>
          </Alert>
          </motion.div>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0, backgroundColor: '#FFFFFF' }}>
          <Button
            onClick={() => setGeneratedToken(null)}
            variant="contained"
            sx={{
              ...buttonStyles.primary,
              backgroundColor: '#27AE60',
              '&:hover': { backgroundColor: '#219A52' },
            }}
          >
            I've Saved the Token
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revoke Token Dialog */}
      <Dialog
        open={openRevokeDialog}
        onClose={() => setOpenRevokeDialog(false)}
        PaperProps={dialogPaperProps}
      >
        <DialogTitle
          sx={{
            fontFamily: 'Bitter, Georgia, serif',
            fontWeight: 600,
            color: '#253E66',
            borderBottom: '1px solid #E5E5E5',
          }}
        >
          Revoke Token
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
          <Typography
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              color: '#253E66',
            }}
          >
            Are you sure you want to revoke this token? This action will disable the token but won't delete it from the system.
          </Typography>
          </motion.div>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0, gap: 2 }}>
          <Button
            onClick={() => setOpenRevokeDialog(false)}
            disabled={loading}
            variant="outlined"
            sx={buttonStyles.cancel}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRevokeToken}
            variant="contained"
            disabled={loading}
            sx={buttonStyles.warning}
            startIcon={loading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <RevokeIcon />}
          >
            {loading ? 'Revoking...' : 'Revoke'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Token Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        PaperProps={dialogPaperProps}
      >
        <DialogTitle
          sx={{
            fontFamily: 'Bitter, Georgia, serif',
            fontWeight: 600,
            color: '#253E66',
            borderBottom: '1px solid #E5E5E5',
          }}
        >
          Delete Token
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
          <Typography
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              color: '#253E66',
            }}
          >
            Are you sure you want to permanently delete this token? This action cannot be undone.
          </Typography>
          </motion.div>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0, gap: 2 }}>
          <Button
            onClick={() => setOpenDeleteDialog(false)}
            disabled={loading}
            variant="outlined"
            sx={buttonStyles.cancel}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteToken}
            variant="contained"
            disabled={loading}
            sx={buttonStyles.danger}
            startIcon={loading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <DeleteIcon />}
          >
            {loading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  );
};

export default SCIMConfiguration;
